import React, { useEffect, useMemo, useState } from "react";
import { api, formatError, romeDateString } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, CheckCircle2, UserCheck, UserX, Plug, ChevronLeft, ChevronRight, Calendar as CalendarIcon, Ban } from "lucide-react";

const STATUS_OPTIONS = [
    { value: "da_confermare", label: "Da confermare" },
    { value: "confermata", label: "Confermata" },
    { value: "arrivato", label: "Arrivato" },
    { value: "no_show", label: "No show" },
    { value: "cancellata", label: "Cancellata" },
];

const SOURCE_OPTIONS = ["Manuale", "Telefono", "Walk-in", "Prenova"];

const STATUS_STYLES = {
    da_confermare: "bg-red-900/30 text-red-300 border-red-700/40",
    confermata: "bg-green-900/30 text-green-300 border-green-700/40",
    arrivato: "bg-blue-900/30 text-blue-300 border-blue-700/40",
    no_show: "bg-amber-900/30 text-amber-300 border-amber-700/40",
    cancellata: "bg-red-900/30 text-red-300 border-red-700/40",
};

const StatusBadge = ({ status }) => {
    const label = STATUS_OPTIONS.find((s) => s.value === status)?.label || status;
    return (
        <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${STATUS_STYLES[status] || "bg-surface-elevated text-elegant border-[#1f2c44]"}`}>
            {label}
        </span>
    );
};

// 4 status buttons sempre visibili, con stato attivo evidenziato.
// In tutti i 4 casi il backend sincronizza con renova360 via API.
const StatusActions = ({ reservation, onConfirm, onStatus, layout = "row", showLabels = false }) => {
    const r = reservation;
    const active = r.status;
    const isCanceled = active === "cancellata";

    // Definizione dei 4 stati
    const ACTIONS = [
        {
            key: "confermata",
            label: "Confermato",
            icon: CheckCircle2,
            // Quando: prenotazione non ancora confermata (da_confermare) o stato post-arrivato che torna indietro
            // Sempre cliccabile per rinotificare Prenova.
            // Tinta: verde quando attiva, rossa pulsante quando da fare
            colorActive: "bg-green-600 text-white border-green-500",
            colorIdle: active === "da_confermare"
                ? "bg-red-600 hover:bg-red-500 text-white border-red-500 animate-pulse"
                : "bg-deep border-[#1f2c44] text-elegant hover:border-green-500 hover:text-green-300",
            onClick: () => onConfirm(r.id),
            disabled: isCanceled, // se annullata, riconferma richiede prima 'undo'
            titleActive: r.confirmed_at ? `Confermata il ${new Date(r.confirmed_at).toLocaleString("it-IT")}` : "Confermata",
            titleIdle: r.prenova_id ? "Conferma e notifica il cliente via renova360" : "Conferma prenotazione",
        },
        {
            key: "arrivato",
            label: "Arrivato",
            icon: UserCheck,
            colorActive: "bg-blue-600 text-white border-blue-500",
            colorIdle: "bg-deep border-[#1f2c44] text-elegant hover:border-blue-500 hover:text-blue-300",
            onClick: () => onStatus(r.id, "arrivato"),
            disabled: isCanceled,
            titleActive: r.arrivato_at ? `Arrivato il ${new Date(r.arrivato_at).toLocaleString("it-IT")}` : "Cliente arrivato",
            titleIdle: "Segna come arrivato (cliente seduto) + sync renova360",
        },
        {
            key: "no_show",
            label: "Non presentato",
            icon: UserX,
            colorActive: "bg-amber-600 text-white border-amber-500",
            colorIdle: "bg-deep border-[#1f2c44] text-elegant hover:border-amber-500 hover:text-amber-300",
            onClick: () => onStatus(r.id, "no_show"),
            disabled: isCanceled,
            titleActive: r.no_show_at ? `Non presentato registrato il ${new Date(r.no_show_at).toLocaleString("it-IT")}` : "Cliente non presentato",
            titleIdle: "Segna come non presentato + sync renova360",
        },
        {
            key: "cancellata",
            label: "Annullato",
            icon: Ban,
            colorActive: "bg-red-700 text-white border-red-500",
            colorIdle: "bg-deep border-[#1f2c44] text-elegant hover:border-red-500 hover:text-red-300",
            onClick: () => onStatus(r.id, "cancellata"),
            disabled: false,
            titleActive: r.cancellata_at ? `Annullata il ${new Date(r.cancellata_at).toLocaleString("it-IT")}` : "Annullata",
            titleIdle: "Annulla prenotazione + sync renova360",
        },
    ];

    const containerCls = layout === "row"
        ? "flex items-center gap-1 flex-shrink-0"
        : "grid grid-cols-2 gap-1";

    return (
        <div className={containerCls} data-testid={`status-actions-${r.id}`}>
            {ACTIONS.map((a) => {
                const Icon = a.icon;
                const isActive = active === a.key;
                const cls = isActive ? a.colorActive : a.colorIdle;
                return (
                    <button
                        key={a.key}
                        onClick={() => {
                            if (a.disabled) return;
                            if (isActive) return; // niente da fare, già in quello stato
                            if (!window.confirm(`Cambiare stato in "${a.label}"?`)) return;
                            a.onClick();
                        }}
                        disabled={a.disabled || isActive}
                        title={isActive ? a.titleActive : a.titleIdle}
                        data-testid={`status-btn-${a.key}-${r.id}`}
                        className={`h-8 ${showLabels ? "px-2" : "w-8"} rounded border font-bold text-[10px] uppercase tracking-wider flex items-center justify-center gap-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${cls}`}
                    >
                        <Icon className="w-3.5 h-3.5" />
                        {showLabels && <span>{a.label}</span>}
                    </button>
                );
            })}
        </div>
    );
};

const fmt = (d) => romeDateString(d);
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const startOfWeek = (d) => {
    const x = new Date(d);
    const day = (x.getDay() + 6) % 7; // Mon=0..Sun=6
    x.setDate(x.getDate() - day);
    return x;
};
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const sameDay = (a, b) => a.toDateString() === b.toDateString();
const monthLabel = (d) => d.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
const dayLabel = (d) => d.toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" });
const HOURS = Array.from({ length: 17 }, (_, i) => i + 7); // 7-23

const Prenotazioni = () => {
    const [view, setView] = useState("day"); // day, week, month
    const [cursorDate, setCursorDate] = useState(new Date());
    const [list, setList] = useState([]);
    const [counts, setCounts] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [tables, setTables] = useState([]);
    const [form, setForm] = useState({
        customer_name: "", phone: "", email: "", date: "",
        time: "20:00", people: 2, notes: "", table_id: "", source: "Manuale",
    });

    // Compute range based on view
    const range = useMemo(() => {
        if (view === "day") return { from: fmt(cursorDate), to: fmt(cursorDate) };
        if (view === "week") {
            const s = startOfWeek(cursorDate);
            return { from: fmt(s), to: fmt(addDays(s, 6)) };
        }
        const s = startOfMonth(cursorDate);
        const e = endOfMonth(cursorDate);
        return { from: fmt(s), to: fmt(e) };
    }, [view, cursorDate]);

    const load = async () => {
        const { data } = await api.get(`/reservations?date_from=${range.from}&date_to=${range.to}`);
        setList(data);
        const { data: c } = await api.get(`/reservations/calendar?date_from=${range.from}&date_to=${range.to}`);
        setCounts(c);
    };
    useEffect(() => { load(); }, [range.from, range.to]);
    useEffect(() => { api.get("/tables").then(({ data }) => setTables(data)); }, []);

    const move = (delta) => {
        const d = new Date(cursorDate);
        if (view === "day") d.setDate(d.getDate() + delta);
        else if (view === "week") d.setDate(d.getDate() + delta * 7);
        else d.setMonth(d.getMonth() + delta);
        setCursorDate(d);
    };

    const openNew = (date) => {
        const target = date ? fmt(date) : fmt(cursorDate);
        setForm({ customer_name: "", phone: "", email: "", date: target, time: "20:00", people: 2, notes: "", table_id: "", source: "Manuale" });
        setShowModal(true);
    };

    const create = async () => {
        try {
            await api.post("/reservations", form);
            toast.success("Prenotazione creata");
            setShowModal(false);
            load();
        } catch (e) { toast.error(formatError(e)); }
    };

    const updateStatus = async (id, status) => {
        try {
            const { data } = await api.patch(`/reservations/${id}`, { status });
            const labels = {
                arrivato: "Arrivato",
                no_show: "Non presentato",
                confermata: "Confermata",
                cancellata: "Cancellata",
                da_confermare: "Da confermare",
            };
            const action = labels[status] || status;
            const isPrenova = data?.is_prenova;
            const syncOk = data?.sync?.ok;
            const syncStatus = data?.sync?.status;

            if (isPrenova && syncOk) {
                const ep = data?.sync?.endpoint_used;
                toast.success(`✓ ${action}. Sincronizzato con renova360.${ep ? ` (${ep})` : ""}`);
            } else if (isPrenova && syncOk === false) {
                toast.warning(`${action} (locale). renova360 non raggiungibile (HTTP ${syncStatus || "?"}). Controlla "Collega renova360 → Log Sync" per i dettagli.`);
            } else if (data?.sync?.skipped) {
                toast.success(`✓ ${action}.`);
            } else {
                toast.success(`✓ ${action}.`);
            }
            if (data?.table_freed) {
                toast.info("Tavolo riportato libero.");
            }
            load();
        } catch (e) { toast.error(formatError(e)); }
    };

    const confirmReservation = async (id) => {
        try {
            const { data } = await api.post(`/reservations/${id}/confirm`);
            const synced = data.sync?.ok;
            const isPrenova = data.is_prenova;
            const status = data.sync?.status;
            if (isPrenova && synced) {
                toast.success("✓ Confermata. Cliente notificato tramite renova360.");
            } else if (isPrenova && !synced) {
                toast.warning(`Confermata localmente, ma renova360 ha risposto: HTTP ${status}. Il cliente potrebbe non ricevere la notifica.`);
            } else {
                toast.success("✓ Prenotazione confermata");
            }
            load();
        } catch (e) { toast.error(formatError(e)); }
    };

    const remove = async (id) => {
        if (!window.confirm("Eliminare la prenotazione?")) return;
        try { await api.delete(`/reservations/${id}`); load(); } catch (e) { toast.error(formatError(e)); }
    };

    const headerLabel = view === "month"
        ? monthLabel(cursorDate)
        : view === "week"
            ? `${fmt(startOfWeek(cursorDate))} → ${fmt(addDays(startOfWeek(cursorDate), 6))}`
            : dayLabel(cursorDate);

    return (
        <div className="space-y-6" data-testid="prenotazioni-page">
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-brand mb-2">Sala</div>
                    <h1 className="font-display text-4xl font-bold text-warm">Prenotazioni</h1>
                </div>
                <button onClick={() => openNew()} data-testid="new-reservation-button" className="h-12 px-5 bg-brand text-deep font-bold rounded-md flex items-center gap-2 hover:bg-brand-light transition-colors">
                    <Plus className="w-4 h-4" /> Nuova Prenotazione
                </button>
            </div>

            {/* Toolbar */}
            <div className="bg-surface border border-[#1f2c44] rounded-lg p-3 flex flex-wrap items-center gap-3 justify-between">
                <div className="flex items-center gap-1">
                    <button onClick={() => move(-1)} data-testid="cal-prev" className="w-10 h-10 rounded border border-[#1f2c44] hover:border-brand flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
                    <button onClick={() => setCursorDate(new Date())} data-testid="cal-today" className="h-10 px-3 rounded border border-[#1f2c44] hover:border-brand text-xs uppercase tracking-wider">Oggi</button>
                    <button onClick={() => move(1)} data-testid="cal-next" className="w-10 h-10 rounded border border-[#1f2c44] hover:border-brand flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
                    <div className="ml-3 font-display text-lg font-bold text-warm capitalize" data-testid="cal-header">{headerLabel}</div>
                </div>
                <div className="flex bg-deep border border-[#1f2c44] rounded-md overflow-hidden">
                    {[
                        { id: "day", label: "Giorno" },
                        { id: "week", label: "Settimana" },
                        { id: "month", label: "Mese" },
                    ].map((v) => (
                        <button key={v.id} onClick={() => setView(v.id)} data-testid={`view-${v.id}`}
                            className={`px-4 h-10 text-xs uppercase tracking-wider ${view === v.id ? "bg-brand text-deep font-bold" : "text-elegant hover:text-warm"}`}>
                            {v.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Views */}
            {view === "day" && <DayView date={cursorDate} reservations={list} tables={tables} onConfirm={confirmReservation} onStatus={updateStatus} onRemove={remove} onAdd={openNew} />}
            {view === "week" && <WeekView startDate={startOfWeek(cursorDate)} reservations={list} counts={counts} onSelect={(d) => { setCursorDate(d); setView("day"); }} onAdd={openNew} />}
            {view === "month" && <MonthView cursor={cursorDate} reservations={list} counts={counts} onSelect={(d) => { setCursorDate(d); setView("day"); }} />}

            {showModal && (
                <ReservationModal form={form} setForm={setForm} tables={tables} onClose={() => setShowModal(false)} onSave={create} />
            )}
        </div>
    );
};

const DayView = ({ date, reservations, tables, onConfirm, onStatus, onRemove, onAdd }) => {
    const dayKey = fmt(date);
    const dayList = reservations.filter((r) => r.date === dayKey).sort((a, b) => a.time.localeCompare(b.time));
    const byHour = HOURS.map((h) => dayList.filter((r) => parseInt(r.time.split(":")[0]) === h));
    return (
        <div className="bg-surface border border-[#1f2c44] rounded-lg overflow-hidden" data-testid="day-view">
            <div className="px-5 py-3 border-b border-[#1f2c44] flex justify-between items-center">
                <div className="text-sm text-elegant"><span className="text-warm font-bold">{dayList.length}</span> prenotazioni · <span className="text-warm font-bold">{dayList.reduce((s, r) => s + r.people, 0)}</span> persone</div>
                <button onClick={() => onAdd(date)} className="text-xs text-brand hover:underline flex items-center gap-1"><Plus className="w-3 h-3" /> Aggiungi</button>
            </div>
            <div className="divide-y divide-[#1f2c44]">
                {HOURS.map((h, i) => (
                    <div key={h} className="grid grid-cols-[60px_1fr] min-h-[64px]">
                        <div className="bg-deep px-3 py-2 text-xs uppercase tracking-wider text-elegant border-r border-[#1f2c44] flex items-start font-mono">{h.toString().padStart(2, "0")}:00</div>
                        <div className="p-2 space-y-1">
                            {byHour[i].length === 0 && <div className="text-[11px] text-elegant/50 italic px-2 py-1">—</div>}
                            {byHour[i].map((r) => (
                                <div key={r.id} className="bg-surface-elevated border border-[#1f2c44] rounded p-2 flex items-center justify-between gap-3 hover:border-brand transition-colors" data-testid={`day-res-${r.id}`}>
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                        <div className="font-display font-bold text-brand text-sm w-12 flex-shrink-0">{r.time}</div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium truncate flex items-center gap-1">
                                                {r.customer_name}
                                                {r.prenova_id && <Plug className="w-3 h-3 text-brand" title="renova360" />}
                                            </div>
                                            <div className="text-[11px] text-elegant truncate">{r.phone} · {r.people}p {r.email && `· ${r.email}`}</div>
                                        </div>
                                        <div className="text-xs text-elegant whitespace-nowrap">T{tables.find((t) => t.id === r.table_id)?.number || "—"}</div>
                                        <StatusBadge status={r.status} />
                                    </div>
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                        <StatusActions reservation={r} onConfirm={onConfirm} onStatus={onStatus} />
                                        <button onClick={() => onRemove(r.id)} title="Elimina prenotazione (solo da gestionale, non sincronizzato)"
                                            data-testid={`delete-res-${r.id}`}
                                            className="w-8 h-8 rounded border border-[#1f2c44] hover:border-red-500 text-red-400 flex items-center justify-center ml-1"><X className="w-3.5 h-3.5" /></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const WeekView = ({ startDate, reservations, counts, onSelect, onAdd }) => {
    const days = Array.from({ length: 7 }, (_, i) => addDays(startDate, i));
    return (
        <div className="grid grid-cols-1 md:grid-cols-7 gap-2" data-testid="week-view">
            {days.map((d) => {
                const key = fmt(d);
                const list = reservations.filter((r) => r.date === key).sort((a, b) => a.time.localeCompare(b.time));
                const c = counts[key] || { total: 0, people: 0 };
                const isToday = sameDay(d, new Date());
                return (
                    <div key={key} className={`bg-surface border rounded-lg p-3 min-h-[300px] ${isToday ? "border-brand" : "border-[#1f2c44]"}`}>
                        <div className="flex items-center justify-between mb-2">
                            <button onClick={() => onSelect(d)} className="text-left">
                                <div className={`text-[10px] uppercase tracking-wider ${isToday ? "text-brand" : "text-elegant"}`}>{d.toLocaleDateString("it-IT", { weekday: "short" })}</div>
                                <div className={`font-display text-2xl font-bold ${isToday ? "text-brand" : "text-warm"}`}>{d.getDate()}</div>
                            </button>
                            <button onClick={() => onAdd(d)} className="w-7 h-7 rounded border border-[#1f2c44] hover:border-brand text-elegant hover:text-brand flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                        </div>
                        {c.total > 0 && (
                            <div className="text-[10px] uppercase tracking-wider text-elegant mb-2">{c.total} pren · {c.people}p</div>
                        )}
                        <div className="space-y-1">
                            {list.slice(0, 6).map((r) => (
                                <div key={r.id} onClick={() => onSelect(d)} className="text-[11px] bg-deep border border-[#1f2c44] rounded p-1.5 hover:border-brand cursor-pointer">
                                    <div className="flex justify-between items-center">
                                        <span className="font-display font-bold text-brand">{r.time}</span>
                                        <span className="text-[9px] text-elegant">{r.people}p</span>
                                    </div>
                                    <div className="truncate">{r.customer_name}</div>
                                </div>
                            ))}
                            {list.length > 6 && <div className="text-[10px] text-elegant text-center">+{list.length - 6} altre</div>}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const MonthView = ({ cursor, reservations, counts, onSelect }) => {
    const start = startOfMonth(cursor);
    const end = endOfMonth(cursor);
    const startWeek = startOfWeek(start);
    const totalDays = Math.ceil(((end - startWeek) / 86400000) + 1);
    const cells = Array.from({ length: Math.ceil(totalDays / 7) * 7 }, (_, i) => addDays(startWeek, i));
    const today = new Date();
    return (
        <div className="bg-surface border border-[#1f2c44] rounded-lg p-3" data-testid="month-view">
            <div className="grid grid-cols-7 gap-1 mb-2">
                {["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"].map((d) => (
                    <div key={d} className="text-[10px] uppercase tracking-wider text-elegant text-center py-2">{d}</div>
                ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
                {cells.map((d, i) => {
                    const key = fmt(d);
                    const c = counts[key];
                    const inMonth = d.getMonth() === cursor.getMonth();
                    const isToday = sameDay(d, today);
                    return (
                        <button key={i} onClick={() => onSelect(d)} data-testid={`month-cell-${key}`}
                            className={`min-h-[80px] rounded p-2 text-left border transition-colors ${
                                isToday ? "bg-brand-soft border-brand" :
                                inMonth ? "bg-deep border-[#1f2c44] hover:border-brand" :
                                "bg-surface-elevated/30 border-transparent text-elegant/40"
                            }`}>
                            <div className={`text-sm font-display font-bold ${isToday ? "text-brand" : ""}`}>{d.getDate()}</div>
                            {c && c.total > 0 && (
                                <div className="mt-1 space-y-0.5">
                                    <div className="text-[10px] font-bold text-brand">{c.total} pren</div>
                                    <div className="flex flex-wrap gap-0.5">
                                        {c.confermata > 0 && <span className="w-1.5 h-1.5 rounded-full bg-brand" title="Confermate" />}
                                        {c.arrivato > 0 && <span className="w-1.5 h-1.5 rounded-full bg-blue-400" title="Arrivati" />}
                                        {c.no_show > 0 && <span className="w-1.5 h-1.5 rounded-full bg-amber-400" title="No show" />}
                                        {c.cancellata > 0 && <span className="w-1.5 h-1.5 rounded-full bg-red-400" title="Cancellate" />}
                                    </div>
                                    <div className="text-[9px] text-elegant">{c.people}p</div>
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>
            <div className="flex flex-wrap gap-3 mt-3 text-[10px] uppercase tracking-wider text-elegant">
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-brand" /> Confermata</span>
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-blue-400" /> Arrivato</span>
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-400" /> No show</span>
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-red-400" /> Cancellata</span>
            </div>
        </div>
    );
};

const ReservationModal = ({ form, setForm, tables, onClose, onSave }) => (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
        <div className="bg-surface border border-[#1f2c44] rounded-lg p-6 max-w-lg w-full" data-testid="new-reservation-modal">
            <div className="flex justify-between items-center mb-6">
                <h2 className="font-display text-2xl font-bold flex items-center gap-2"><CalendarIcon className="w-5 h-5 text-brand" /> Nuova Prenotazione</h2>
                <button onClick={onClose} className="text-elegant hover:text-warm"><X /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input className="h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none sm:col-span-2"
                    placeholder="Nome cliente" value={form.customer_name}
                    onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="res-name" />
                <input className="h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none"
                    placeholder="Telefono" value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="res-phone" />
                <input className="h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none"
                    placeholder="Email" value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="res-email" />
                <input type="date" className="h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none"
                    value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="res-date" />
                <input type="time" className="h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none"
                    value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="res-time" />
                <input type="number" className="h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none"
                    placeholder="Persone" value={form.people} min={1}
                    onChange={(e) => setForm({ ...form, people: parseInt(e.target.value) || 1 })} data-testid="res-people" />
                <select className="h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none"
                    value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} data-testid="res-source">
                    {SOURCE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select className="h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none sm:col-span-2"
                    value={form.table_id} onChange={(e) => setForm({ ...form, table_id: e.target.value })} data-testid="res-table">
                    <option value="">— Tavolo (opzionale) —</option>
                    {tables.map((t) => <option key={t.id} value={t.id}>Tavolo {t.number} ({t.seats}p)</option>)}
                </select>
                <textarea className="h-20 p-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none sm:col-span-2"
                    placeholder="Note" value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="res-notes" />
            </div>
            <button onClick={onSave} data-testid="save-reservation-button"
                className="w-full h-14 mt-5 bg-brand text-deep font-bold uppercase tracking-wider rounded-md hover:bg-brand-light transition-colors">
                Conferma Prenotazione
            </button>
        </div>
    </div>
);

export default Prenotazioni;
